import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { TbArrowBackUp } from "react-icons/tb";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import WarningModal from "../modals/WarningModal";
import AddEditSectionModal from "./AddEditSectionModal";

const FeaturedSectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [loader, setLoader] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);

  const fetchSection = async () => {
    if (!id) return;
    try {
      setLoader(true);
      const result = await apiClient.get(`/featured-sections/${id}/`);
      setLoader(false);
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      setSection(result.data);
    } catch (error) {
      setLoader(false);
      toast.error("Failed to load section");
    }
  };

  useEffect(() => {
    fetchSection();
  }, [id]);

  const confirmDelete = async () => {
    if (!id) return;
    try {
      setDeleteLoader(true);
      const result = await apiClient.delete(`/featured-sections/${id}/`);
      setDeleteLoader(false);
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      toast.success("Section deleted successfully");
      navigate("/admin/featured-sections");
    } catch (error) {
      setDeleteLoader(false);
      toast.error("Failed to delete section");
    }
  };

  const handleEditSaved = () => {
    setShowEditModal(false);
    fetchSection();
  };

  const mockups = section?.mockups ?? section?.listings ?? [];
  const thumb = (item) => {
    const listing = item?.listing ?? item;
    return listing?.use_main_file_as_thumbnail
      ? listing?.compressed_file
      : listing?.thumbnails?.[0]?.image;
  };
  const title = (item) => (item?.listing ?? item)?.title ?? "—";
  const listingId = (item) => (item?.listing ?? item)?.id;

  if (loader) {
    return (
      <div className="container py-5 d-flex justify-content-center">
        <LoadingSpinner color="#FF3E1F" />
      </div>
    );
  }

  if (!section) {
    return (
      <div className="container py-5 text-center text-muted">
        <p>Section not found.</p>
        <div
          className="d-inline-flex align-items-center gap-2 mt-2"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/admin/featured-sections")}
        >
          <TbArrowBackUp
            style={{ fontSize: "32px" }}
            className="shadow rounded-5 p-1 bg-light"
          />
          <span>Back to Featured Sections</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center mb-4">
        <div
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/admin/featured-sections")}
        >
          <TbArrowBackUp
            style={{ fontSize: "32px" }}
            className="shadow me-2 rounded-5 p-1 bg-light"
          />
        </div>
        <h2 className="mb-0 flex-grow-1" style={{ color: "#000000e6" }}>
          {section.name}
        </h2>
        <button
          type="button"
          className="btn"
          style={{
            backgroundColor: "#0e0e0e",
            color: "#fff",
            border: "none",
            fontWeight: "600",
            padding: "6px 32px",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "15px",
          }}
          onClick={() => setShowEditModal(true)}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn"
          style={{
            backgroundColor: "#c93312",
            color: "#fff",
            border: "none",
            fontWeight: "600",
            padding: "6px 24px",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "15px",
          }}
          onClick={() => setShowDeleteConfirm(true)}
        >
          Delete
        </button>
      </div>

      {section.description && (
        <p className="text-muted mb-4">{section.description}</p>
      )}

      <h6 className="mb-3">Mockups ({mockups.length})</h6>
      {mockups.length === 0 ? (
        <p className="text-muted">No mockups in this section.</p>
      ) : (
        <div className="row">
          {mockups.map((item) => {
            const lid = listingId(item);
            return (
              <div key={item.id ?? lid} className="col-6 col-md-4 col-lg-3 mb-4">
                <Link
                  to={lid ? `/mockup?id=${lid}` : "#"}
                  className="text-decoration-none text-dark"
                >
                  <div className="card shadow-sm h-100 border-0 rounded-3 overflow-hidden">
                    <div className="card-img-wrapper">
                      <img
                        src={thumb(item) || ""}
                        alt=""
                        className="card-img-top"
                        style={{
                          height: "200px",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                    </div>
                    <div className="card-body p-2">
                      <h6
                        className="card-title small text-truncate mb-0"
                        title={title(item)}
                      >
                        {title(item)}
                      </h6>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      <AddEditSectionModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSaved={handleEditSaved}
        section={section}
      />

      <WarningModal
        showModal={showDeleteConfirm}
        modalClose={() => {
          if (!deleteLoader) {
            setShowDeleteConfirm(false);
          }
        }}
        setConfirmClose={confirmDelete}
        confirmDisabled={deleteLoader}
        confirmLabel={deleteLoader ? "Deleting..." : "Delete"}
        text={`Are you sure you want to delete "${section.name}"?`}
      />
    </div>
  );
};

export default FeaturedSectionDetail;
